/**
 * Re-probes the API when the app returns to the foreground.
 *
 * Mobile browsers suspend the health-check interval while the tab is in the
 * background, so a ping that failed then leaves the connectivity machine
 * "down". No `online` event follows (the network did not change), so nothing
 * corrects it and the server-down banner stays until a manual refresh. On
 * return we resync the network flag and force an immediate probe, without
 * reloading the page.
 *
 * Triggers: `visibilitychange` (app switch), `pageshow` (iOS Safari bfcache
 * restoration) and `focus`. The recovery itself is pinned in
 * `packages/shared/src/powersync/__tests__/manager.test.ts`.
 */
export interface ForegroundReprobeTarget {
  handlers: { onNetworkChanged(online: boolean): void };
  forceHealthCheck(): Promise<void>;
}

/** Attaches the listeners. Returns the function that detaches them. */
export function attachForegroundReprobe(manager: ForegroundReprobeTarget): () => void {
  const handleForeground = () => {
    if (document.visibilityState !== "visible") {
      return;
    }
    manager.handlers.onNetworkChanged(navigator.onLine);
    void manager.forceHealthCheck();
  };

  document.addEventListener("visibilitychange", handleForeground);
  globalThis.addEventListener("pageshow", handleForeground);
  globalThis.addEventListener("focus", handleForeground);

  return () => {
    document.removeEventListener("visibilitychange", handleForeground);
    globalThis.removeEventListener("pageshow", handleForeground);
    globalThis.removeEventListener("focus", handleForeground);
  };
}
